/**
 * A gathering's setup as a shareable JSON file — download it on one node,
 * import it on another. Only portable things go in: the ideal room, groups,
 * topics and flow. Never people, their picks, published rooms, seeds or run
 * state, which only mean something on the node they came from.
 *
 * The backend validates imports (schemas/gatherings.py GatheringConfig — keep
 * the two in step); readGatheringConfig() here is a friendly first check so
 * the admin gets a preview, or a clear reason, before anything is sent.
 */

export const CONFIG_FORMAT = 'vellby.gathering';
export const CONFIG_VERSION = 1;

/** The config for what the admin view currently shows. */
export function buildGatheringConfig({ roomSize, poolMode, groups, seats, topics, flow, nodeName, now = new Date() }) {
    return {
        format: CONFIG_FORMAT,
        version: CONFIG_VERSION,
        exported_at: now.toISOString(),
        source_node: nodeName || null,
        room: { size: roomSize, pool: poolMode },
        groups: groups.map(g => ({ name: g.name, color: g.color ?? 0, seats_per_room: seats[g.id] ?? g.seats_per_room ?? 0 })),
        topics: topics.map(t => t.text),
        flow: flow.map(s => ({ kind: s.kind, minutes: s.duration_minutes })),
    };
}

export function configFilename(config) {
    const node = (config.source_node || 'node').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return `gathering-${node || 'node'}-${config.exported_at.slice(0, 10)}.json`;
}

/**
 * Parse a file's text into a config, or explain why it isn't one.
 * @returns {{ config } | { error }}
 */
export function readGatheringConfig(text) {
    let data;
    try {
        data = JSON.parse(text);
    } catch {
        return { error: 'This file isn’t valid JSON.' };
    }
    if (!data || typeof data !== 'object' || data.format !== CONFIG_FORMAT) {
        return { error: 'This isn’t a gathering configuration file.' };
    }
    if (data.version !== CONFIG_VERSION) {
        return { error: `This file is version ${data.version}; this node reads version ${CONFIG_VERSION}.` };
    }
    if (!Array.isArray(data.flow) || data.flow.length === 0) {
        return { error: 'The file has no flow steps.' };
    }
    return { config: { ...data, groups: data.groups ?? [], topics: data.topics ?? [] } };
}

/** Download a config as a .json file. */
export function downloadGatheringConfig(config) {
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = configFilename(config);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Whether a gathering (admin API data) has never been set up — no groups, no
 * topics, nothing published or running, and the flow still the backend's
 * default single breakout round (pods/gatherings/service.py DEFAULT_FLOW).
 * That's when the admin view offers to design from scratch or import.
 */
export function isBlankSetup(data) {
    if (!data) return false;
    const flow = data.flow ?? [];
    return (data.groups ?? []).length === 0
        && (data.topics ?? []).length === 0
        && !data.published_at
        && !data.flow_started_at
        && flow.length === 1 && flow[0].kind === 'breakout' && flow[0].duration_minutes === 30;
}
